import { NavLink, Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, FileVideo, CreditCard, Users, Settings,
  Shield, Mail, TrendingUp, ScrollText, Inbox, Flag, Package
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import axios from "axios";

const API = `${process.env.REACT_APP_API_URL}/api`;

const NAV_GROUPS = [
  {
    label: "Operations",
    items: [
      { to: "/admin",              end: true,  icon: LayoutDashboard, label: "Dashboard"    },
      { to: "/admin/users",        end: false, icon: Users,           label: "Users"        },
      { to: "/admin/analyses",     end: false, icon: FileVideo,       label: "Analyses"     },
      { to: "/admin/reports",      end: false, icon: Flag,            label: "Flag Reports" },
    ]
  },
  {
    label: "Finance",
    items: [
      { to: "/admin/finance",      end: false, icon: TrendingUp,      label: "Finance"      },
      { to: "/admin/transactions", end: false, icon: CreditCard,      label: "Transactions" },
      { to: "/admin/packages",     end: false, icon: Package,         label: "Packages"     },
    ]
  },
  {
    label: "Marketing",
    items: [
      { to: "/admin/email-blast",  end: false, icon: Mail,            label: "Email Blast"  },
    ]
  },
  {
    label: "Admin",
    items: [
      { to: "/admin/audit-log",    end: false, icon: ScrollText,      label: "Audit Log"    },
      { to: "/admin/system",       end: false, icon: Settings,        label: "System"       },
    ]
  },
  {
    label: "Developer",
    items: [
      { to: "/admin/dev-emails",   end: false, icon: Inbox,           label: "Dev Emails"   },
    ]
  },
];

// Flat list for mobile
const NAV_FLAT = NAV_GROUPS.flatMap(g => g.items);

export default function AdminLayout() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [pendingReports, setPendingReports] = useState(0);

  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        const { data } = await axios.get(`${API}/admin/content-reports/pending-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPendingReports(data.count || 0);
      } catch {
        // silently ignore — badge is non-critical
      }
    };
    fetchPendingCount();
    // Refresh every 60s
    const interval = setInterval(fetchPendingCount, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-52 border-r border-border bg-background shrink-0 sticky top-[72px] h-[calc(100vh-72px)]">
        <div className="px-4 py-5 border-b border-border">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-brand-primary" />
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Admin Panel</span>
          </div>
          <p className="text-xs font-mono text-muted-foreground/60 mt-1 truncate">{user?.email}</p>
        </div>
        <nav className="flex-1 px-2 py-3 space-y-4 overflow-y-auto">
          {NAV_GROUPS.map((group) => (
            <div key={group.label}>
              <p className="px-3 mb-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground/40">
                {group.label}
              </p>
              <div className="space-y-0.5">
                {group.items.map(({ to, end, icon: Icon, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm transition-colors ${
                        isActive
                          ? "bg-brand-primary/10 text-brand-primary font-medium"
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                      }`
                    }
                    data-testid={`admin-nav-${label.toLowerCase().replace(" ", "-")}`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="flex-1">{label}</span>
                    {label === "Flag Reports" && pendingReports > 0 && (
                      <span
                        className="ml-auto min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold font-mono"
                        data-testid="pending-reports-badge"
                      >
                        {pendingReports > 99 ? "99+" : pendingReports}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-border text-xs font-mono text-muted-foreground/40">
          Veriksa v2.0
        </div>
      </aside>

      {/* Mobile tab bar - show only key items */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background/95 backdrop-blur-sm flex">
        {NAV_FLAT.filter(n => ["Dashboard","Users","Analyses","Finance","Packages","System"].includes(n.label)).map(({ to, end, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center py-2 text-[10px] font-mono transition-colors ${
                isActive ? "text-brand-primary" : "text-muted-foreground"
              }`
            }
          >
            <Icon className="w-4 h-4 mb-0.5" />
            {label}
          </NavLink>
        ))}
      </div>

      {/* Main content */}
      <main className="flex-1 overflow-auto pb-16 md:pb-0">
        <Outlet />
      </main>
    </div>
  );
}
